import type { TFunction } from 'i18next';

/**
 * What the server holds back from a fund's available balance for its support requests. The fund's
 * balance (`supportRequestsBalances`) and a management-board row both carry these fields by name.
 */
export interface PendingSupportsBreakdown {
  pendingExecutionSumSingleMonth?: number;
  pendingExecutionSumMultiMonth?: number;
  frozenRequestsSum?: number;
  /** HAT-1396: the fee APT charges on each pending support, and its total (held back next to the sums above). */
  supportRowFee?: number;
  pendingSupportFeesSum?: number;
  /** SPR-10304: standing supports due in the coming days - shown, not held back. */
  upcomingStandingSupports?: { sum: number }[];
}

/** SPR-10304: total of the standing supports due in the coming days that the balance does not hold back. */
export function sumUpcomingStandingSupports(pending: PendingSupportsBreakdown | null | undefined): number {
  return (pending?.upcomingStandingSupports ?? []).reduce((total, u) => total + (u.sum ?? 0), 0);
}

/** Multiline tooltip for "available balance" pending execution breakdown (InfoPanel uses pre-line). */
export function buildAvailableBalancePendingTooltipText(
  t: TFunction,
  formatAmount: (n: number) => string,
  pending: PendingSupportsBreakdown | null | undefined,
): string {
  const feesSum = pending?.pendingSupportFeesSum ?? 0;
  const upcomingSum = sumUpcomingStandingSupports(pending);
  return [
    t('availableBalancePendingIntro'),
    t('pendingExecutionSingleMonthLine', { amount: formatAmount(pending?.pendingExecutionSumSingleMonth ?? 0) }),
    t('pendingExecutionMultiMonthLine', { amount: formatAmount(pending?.pendingExecutionSumMultiMonth ?? 0) }),
    ...(feesSum > 0
      ? [t('supportOverheadLine', { fee: formatAmount(pending?.supportRowFee ?? 0), amount: formatAmount(feesSum) })]
      : []),
    t('frozenRequestsLine', { amount: formatAmount(pending?.frozenRequestsSum ?? 0) }),
    t('availableBalanceNoteClosing'),
    // The standing supports about to fall due are deliberately outside the deduction, so the
    // coordinator is told they exist and are not yet taken off the number above.
    ...(upcomingSum > 0 ? [t('upcomingStandingSupportsTooltipLine', { amount: formatAmount(upcomingSum) })] : []),
  ].join('\n');
}
